import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { Trans, useTranslation } from "react-i18next";
import { Tooltip, message } from "antd";
import Icon, { LinkOutlined } from "@ant-design/icons";
import classNames from "classnames";
import { Permissions } from "@whatsper/texterchat-common";
import { useStore } from "../../../../store/rootStore";
import { Operation } from "../../../EntityManager";
import { ManagerListView } from "../core";
import { getChannelSharableLink, getDirection } from "../../../../helpers";
import { getConfig } from "../../../../settings";
import styles from "./ChannelsListView.module.scss";
import entityManagerStyles from "../../../EntityManager/EntityManager.module.scss";
const ChannelTeaser = observer(({ entity }) => {
    var _a;
    const { channels } = useStore();
    const { title, channel, metadata, accountId } = entity;
    const icon = (_a = channels.themes[accountId]) === null || _a === void 0 ? void 0 : _a.icon;
    return (React.createElement("div", { className: styles.teaserContainer },
        React.createElement("img", { src: icon, alt: "icon", className: styles.teaserIcon }),
        React.createElement("div", { className: styles.teaserContent },
            React.createElement("div", { className: styles.teaserName },
                channel,
                " (",
                title || metadata.accountTitle || metadata.pageName || metadata.businessName || accountId,
                ")"),
            React.createElement("div", { className: styles.teaserNumber }, accountId))));
});
const ChannelsListView = ({ onEntitySelect, onEntityDelete, searchQuery = "", refreshList }) => {
    const { channels, agentsStore } = useStore();
    const { t } = useTranslation();
    const [inboxConfig, setInboxConfig] = useState(null);
    const channelsSearchQuery = searchQuery.trim().toLowerCase();
    useEffect(() => {
        getConfig()
            .then((config) => setInboxConfig(config))
            .catch((error) => console.error("Error getting inbox config", error));
    }, []);
    useEffect(() => {
        refreshList === null || refreshList === void 0 ? void 0 : refreshList(channels.fetchChannelsAccounts);
    }, [refreshList]);
    const getOpSupported = () => {
        var _a;
        const { hasPermissions } = agentsStore;
        const ops = [];
        if (hasPermissions([Permissions.Permission.channels_view])) {
            ops.push(Operation.LIST);
        }
        if (hasPermissions([Permissions.Permission.channels_manage])) {
            ops.push(Operation.CREATE, Operation.EDIT);
            if ((_a = inboxConfig === null || inboxConfig === void 0 ? void 0 : inboxConfig.features) === null || _a === void 0 ? void 0 : _a.channelsDelete) {
                ops.push(Operation.DELETE);
            }
        }
        return ops;
    };
    const filteredChannels = channels.accounts.slice().filter((channel) => {
        var _a, _b, _c;
        if (!channelsSearchQuery)
            return true;
        const title = ((_a = channel.title) === null || _a === void 0 ? void 0 : _a.toLowerCase()) || "";
        const channelName = ((_b = channel.channel) === null || _b === void 0 ? void 0 : _b.toLowerCase()) || "";
        const accountId = ((_c = channel.accountId) === null || _c === void 0 ? void 0 : _c.toLowerCase()) || "";
        return title.includes(channelsSearchQuery) || channelName.includes(channelsSearchQuery) || accountId.includes(channelsSearchQuery);
    });
    const customListActions = (channel) => {
        const onClick = (e) => {
            e.stopPropagation();
            if (window.isSecureContext) {
                const url = getChannelSharableLink({
                    name: channel.channel,
                    id: channel.accountId,
                });
                navigator.clipboard.writeText(url);
                message.success(t("channelLinkCopiedToClipboard"));
            }
            else {
                message.error(t("clipboardNotSupported"));
            }
        };
        return (React.createElement(Tooltip, { title: t("copyLinkToChat") },
            React.createElement(Icon, { className: classNames(styles.copyIconBtn, entityManagerStyles.entityListItemBtn, {
                    [entityManagerStyles.rtl]: getDirection() === "rtl",
                }), component: LinkOutlined, onClick: onClick })));
    };
    return (React.createElement(ManagerListView, { entities: filteredChannels, entityIdKey: "_id", teaser: ChannelTeaser, loading: channels.loading, opSupported: getOpSupported(), onEntitySelect: onEntitySelect, onEntityDelete: onEntityDelete, createTitle: React.createElement(Trans, { i18nKey: "addChannel" }), customListActions: customListActions }));
};
export default observer(ChannelsListView);
